import type { RefObject } from 'react';
import { useEffect, useState } from 'react';
import { useIsMounted } from '~/Utils/Hooks/useIsMounted';

export interface UseCollapseProps {
    isOpen: boolean;
    content: RefObject<HTMLDivElement>;
}

// In case of nested collapse components, we need to inherit the visibility from the parent so that
// focusable children inside expanded collapses nested inside collapsed collapses are not focusable.
// Unfortunately, jest is not able to simulate this behavior thus we are not able to test it.
type InheritVisibility = undefined;

type Visibility = 'hidden' | InheritVisibility;
type Overflow = 'hidden' | 'visible';

const getHeight = (content: RefObject<HTMLDivElement>) => {
    if (content && content.current && content.current.scrollHeight) {
        return `${content.current.scrollHeight}px`;
    }

    return '0px';
};

export const useCollapse = (props: UseCollapseProps) => {
    const { isOpen, content } = props;

    const [height, setHeight] = useState(isOpen ? 'auto' : '0');
    const [overflow, setOverflow] = useState<Overflow>(isOpen ? 'visible' : 'hidden');
    const [visibility, setVisibility] = useState<Visibility>(isOpen ? undefined : 'hidden');
    const isMounted = useIsMounted();

    const toggleStyles = () => {
        if (isOpen) {
            setHeight('auto');
            setOverflow('visible');
            setVisibility(undefined);
        } else {
            setVisibility('hidden');
        }
    };

    useEffect(() => {
        if (isOpen) {
            setVisibility(undefined);

            if (!isMounted) {
                setHeight('auto');
                setOverflow('visible');
            } else {
                setHeight(getHeight(content));
            }
        } else if (isMounted) {
            setHeight(getHeight(content));

            // The magic:
            // Setting collapsed style after setting height to enable smooth transition based on height.
            // It must be wrapped with setTimeout to ensure that the state update is not batched with the height update.
            setTimeout(() => {
                window.requestAnimationFrame(() => {
                    setOverflow('hidden');
                    setHeight('0');
                });
            });
        }
    }, [isOpen]);

    return {
        toggleStyles,
        style: {
            overflow,
            visibility,
            height,
        },
    };
};
